(() => {
  if (window.__watcFreeGiftLoaded) return;
  window.__watcFreeGiftLoaded = true;

  const COMPONENT_SELECTOR = 'watc-free-gift-selector';
  const GIFT_PROPERTY = '_watc_free_gift';

  let cartIsUpdating = false;
  let syncTimer = null;

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  async function requestCart(path, options = {}) {
    const response = await fetch(`${routesRoot()}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
          data.message ||
          'The basket could not be updated. Please try again.'
      );
    }

    return data;
  }

  function currentDiscountCodes() {
    return [
      ...document.querySelectorAll(
        '.cart-discount__pill[data-discount-code]'
      ),
    ]
      .map((element) => element.dataset.discountCode?.trim())
      .filter(Boolean);
  }

  function uniqueDiscountCodes(codes) {
    const uniqueCodes = new Map();

    codes.forEach((code) => {
      const cleanedCode = String(code || '').trim();

      if (cleanedCode) {
        uniqueCodes.set(cleanedCode.toLowerCase(), cleanedCode);
      }
    });

    return [...uniqueCodes.values()];
  }

  function setStatus(component, message = '', isError = false) {
    const status = component?.querySelector('[data-watc-gift-status]');

    if (!status) return;

    status.textContent = message;

    if (isError) {
      status.dataset.error = 'true';
    } else {
      delete status.dataset.error;
    }
  }

  function setButtonsBusy(component, isBusy) {
    if (!component) return;

    component.setAttribute('aria-busy', String(isBusy));

    component
      .querySelectorAll('[data-watc-select-gift]')
      .forEach((button) => {
        if (isBusy) {
          button.disabled = true;
        } else {
          button.disabled = button.dataset.selected === 'true';
        }
      });
  }

  async function fetchRenderedSection(sectionId) {
    if (!sectionId) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    const sections = await response.json();

    return { sections };
  }

  function replaceRenderedSection(responseData, sectionId) {
    const html = responseData?.sections?.[sectionId];

    if (!html || !sectionId) return false;

    const wrapperId = `shopify-section-${sectionId}`;
    const currentSection = document.getElementById(wrapperId);

    if (!currentSection) return false;

    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const replacementSection =
      parsedDocument.getElementById(wrapperId) ||
      parsedDocument.body.firstElementChild;

    if (!replacementSection) return false;

    currentSection.replaceWith(replacementSection);

    return true;
  }

  function openCartDrawer() {
    window.setTimeout(() => {
      const dialog = document.querySelector('.cart-drawer__dialog');

      if (dialog?.open) return;

      const trigger = document.querySelector(
        '[data-testid="cart-drawer-trigger"]'
      );

      if (trigger) {
        trigger.click();
        return;
      }

      const drawer = document.querySelector('cart-drawer-component');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    }, 80);
  }

  function reloadWithDrawerOpen() {
    sessionStorage.setItem('watc-reopen-cart', 'true');
    window.location.reload();
  }

  async function renderUpdatedCart(component, responseData = null) {
    const sectionId = component?.dataset.sectionId;

    let renderedResponse = responseData;

    if (!renderedResponse?.sections?.[sectionId]) {
      renderedResponse = await fetchRenderedSection(sectionId);
    }

    const sectionWasReplaced = replaceRenderedSection(
      renderedResponse,
      sectionId
    );

    if (!sectionWasReplaced) {
      reloadWithDrawerOpen();
      return;
    }

    openCartDrawer();
  }

  async function updateDiscountAndRender(
    component,
    discountCode,
    shouldAddCode
  ) {
    const sectionId = component.dataset.sectionId;
    const normalisedGiftCode = discountCode.toLowerCase();

    let codes = currentDiscountCodes().filter(
      (code) => code.toLowerCase() !== normalisedGiftCode
    );

    if (shouldAddCode) {
      codes.push(discountCode);
    }

    codes = uniqueDiscountCodes(codes);

    return requestCart('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        discount: codes.join(','),
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    });
  }

  async function selectGift(button) {
    if (cartIsUpdating) return;

    const component = button.closest(COMPONENT_SELECTOR);
    const variantId = Number(button.dataset.variantId);
    const productTitle = button.dataset.productTitle || 'gift';
    const discountCode = component?.dataset.discountCode?.trim() || '';

    if (!component || !variantId) return;

    cartIsUpdating = true;
    setButtonsBusy(component, true);
    setStatus(component, `Adding ${productTitle}…`);

    try {
      const cart = await requestCart('cart.js');
      const existingGifts = cart.items.filter(
        (item) => item.properties?.[GIFT_PROPERTY] === 'true'
      );

      const sameGift = existingGifts.find(
        (item) => Number(item.variant_id) === variantId
      );

      if (sameGift) {
        setStatus(component, `${productTitle} is already selected.`);
        return;
      }

      for (const existingGift of existingGifts) {
        await requestCart('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0,
          }),
        });
      }

      await requestCart('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'true',
              },
            },
          ],
        }),
      });

      let renderedResponse = null;

      if (discountCode) {
        renderedResponse = await updateDiscountAndRender(
          component,
          discountCode,
          true
        );
      }

      await renderUpdatedCart(component, renderedResponse);

      document.dispatchEvent(
        new CustomEvent('watc:free-gift-updated', {
          detail: {
            variantId,
            productTitle,
          },
        })
      );
    } catch (error) {
      console.error('[WATC free gift]', error);
      setStatus(
        component,
        error.message ||
          'The gift could not be added. Please try again.',
        true
      );
    } finally {
      cartIsUpdating = false;

      if (component.isConnected) {
        setButtonsBusy(component, false);
      }
    }
  }

  async function removeIneligibleGift(component) {
    if (cartIsUpdating) return;

    cartIsUpdating = true;

    try {
      const cart = await requestCart('cart.js');
      const markedGifts = cart.items.filter(
        (item) => item.properties?.[GIFT_PROPERTY] === 'true'
      );

      for (const gift of markedGifts) {
        await requestCart('cart/change.js', {
          method: 'POST',
          body: JSON.stringify({
            id: gift.key,
            quantity: 0,
          }),
        });
      }

      const discountCode =
        component.dataset.discountCode?.trim() || '';

      let renderedResponse = null;

      if (discountCode) {
        renderedResponse = await updateDiscountAndRender(
          component,
          discountCode,
          false
        );
      }

      await renderUpdatedCart(component, renderedResponse);
    } catch (error) {
      console.error('[WATC free gift cleanup]', error);
    } finally {
      cartIsUpdating = false;
    }
  }

  async function removeOrphanedGiftCode(component) {
    if (cartIsUpdating) return;

    const discountCode =
      component.dataset.discountCode?.trim() || '';

    if (!discountCode) return;

    const giftCodeIsApplied = currentDiscountCodes().some(
      (code) => code.toLowerCase() === discountCode.toLowerCase()
    );

    if (!giftCodeIsApplied) return;

    cartIsUpdating = true;

    try {
      const renderedResponse = await updateDiscountAndRender(
        component,
        discountCode,
        false
      );

      await renderUpdatedCart(component, renderedResponse);
    } catch (error) {
      console.error('[WATC gift discount cleanup]', error);
    } finally {
      cartIsUpdating = false;
    }
  }

  function synchroniseGiftState() {
    if (cartIsUpdating) return;

    const component = document.querySelector(COMPONENT_SELECTOR);

    if (!component) return;

    if (component.dataset.removeIneligible === 'true') {
      removeIneligibleGift(component);
      return;
    }

    const selectedGiftKey =
      component.dataset.selectedGiftKey?.trim() || '';

    if (!selectedGiftKey) {
      removeOrphanedGiftCode(component);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-watc-select-gift]');

    if (!button) return;

    event.preventDefault();
    selectGift(button);
  });

  document.addEventListener('cart:update', () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(synchroniseGiftState, 150);
  });

  const observer = new MutationObserver(() => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(synchroniseGiftState, 150);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  function initialise() {
    synchroniseGiftState();

    if (sessionStorage.getItem('watc-reopen-cart') === 'true') {
      sessionStorage.removeItem('watc-reopen-cart');
      openCartDrawer();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, {
      once: true,
    });
  } else {
    initialise();
  }
})();
